import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { StyledBox, StyledTypography } from "../../../styled-mui";
import { getOrderItemPriceData, getPrices, getProductPriceData } from "../../../utils/priceUtil";
import { PRODUCT_TYPE } from "../../../constants/catalog";
import { getDateMonth } from "../../../utils/storeLocatorUtils";
import storeUtil from "../../../utils/storeUtil";
import { palette } from "../../../themes/color-palette";
import { StyledFormattedPriceDisplay } from "./styled-formattedPriceDisplay";
import { truncateNumberToDecimals } from "../../../utils/genericUtil";
import RemisedPriceDropIcon from "../../svg/remisedPriceDrop";
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const setTwoDecimal = (price, sup = true) => {
  if (!price) {
    return <></>;
  }
  const start = price.toString().split(".")[0];
  const end = price.toString().split(".")[1];
  const endFormatted = end != null ? (end.length === 1 ? `${end}0` : end.substring(0, 2)) : "00";

  return (
    <>
      {start}
      {sup ? <sup>{`,${endFormatted}€`}</sup> : `,${endFormatted}€`}
    </>
  );
};

interface SharedComponentProps {
  currency?: string;
  language?: string;
  variant?: string;
  showNoPriceFoundTxt?: boolean;
  hideLeadInTxt?: boolean;
  hideCrossedPrice?: boolean;
  hidePromoPercentage?: boolean;
  hideM2TextDesc?: boolean;
  isPLP?: boolean;
}
interface ProductProps extends SharedComponentProps {
  product: Record<string, any> | null;
  orderItem?: never;
}
interface OrderItemProps extends SharedComponentProps {
  product?: never;
  orderItem: Record<string, any> | null;
}
type PriceDisplayProps = ProductProps | OrderItemProps;

//TYPESCRIPT WILL THROW ERROR IF BOTH product AND orderItem ARE PROVIDED TOGETHER
export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  currency = "EUR",
  language = "fr-FR",
  variant = "h1",
  showNoPriceFoundTxt = false,
  product = null,
  orderItem = null,
  hideLeadInTxt = false,
  hideCrossedPrice = false,
  hidePromoPercentage = false,
  hideM2TextDesc = false,
  isPLP = false
}) => {
  const { t } = useTranslation();
  const IntlPrice = Intl.NumberFormat(language, {
    style: "currency",
    currency,
  });
  const IntlUnit = Intl.NumberFormat(language, {
    style: "unit",
    unit: "meter",
  });

  const {
    markedPrice,
    promoPrice,
    promoPercentage,
    isPromo,
    ecoPart,
    remisedPriceDetails,
    type,
    meterSize,
    meterPrice,
  } = getProductPriceData(product as Record<any, any>);

  const {
    markedPrice: orderMarkedPrice,
    promoPrice: orderPromoPrice,
    // unitMarkedPricePerOneM2,
    // unitPromoPricePerOneM2,
    // unitMarkedPrice,
    // unitPromoPrice,
    // unitEcoPart,
    ecoPart: orderEcoPart,
    promoPercentage: orderPromoPercentage,
    meterSize: orderMeterSize,
    meterPrice: orderMeterPrice,
    productType: orderProductType,
    isPromo: isOrderPromo,
  } = getOrderItemPriceData(orderItem as Record<any, any>);

  const TYPE = type || orderProductType || null;
  const PRICE_LEAD_IN_TEXT = TYPE === PRODUCT_TYPE.SUR_MESURE ? t("PriceDisplay.StartInfo.CustomMade") : null;

  const IS_PROMOTION: boolean = isPromo || isOrderPromo || false;
  const PROMO_PERCENTAGE = promoPercentage || orderPromoPercentage || null;

  const MARKED_PRICE = markedPrice || orderMarkedPrice || null;
  const PROMO_PRICE = promoPrice || orderPromoPrice || null;
  const FINAL_PRICE = (IS_PROMOTION ? PROMO_PRICE : MARKED_PRICE) || null;

  const METER_SIZE = meterSize || orderMeterSize || null;
  let METER_PRICE = meterPrice || orderMeterPrice || null;
  METER_PRICE = METER_PRICE ? truncateNumberToDecimals(METER_PRICE, 3) : null;

  const ECOPART = ecoPart || orderEcoPart || null;

  const IS_REMISED_PRICE_VALID = remisedPriceDetails?.valid || false;
  const REMISED_OLD_PRICE_END_DATE = remisedPriceDetails?.OldPriceEnd || null;
  const REMISED_OLD_PRICE = remisedPriceDetails?.oldPrice || null;

  const COLOR = IS_PROMOTION ? palette.primary.main : palette.text.primary;

  if (showNoPriceFoundTxt && !FINAL_PRICE) {
    return (
      <StyledFormattedPriceDisplay className="price price-not-found">
        <StyledTypography
          variant="body4"
          component="p"
          color={COLOR}
          pb={1}
          className="price-unavailable"
          sx={{ height: 92 }}>
          Produit disponible uniquement en magasin
        </StyledTypography>
      </StyledFormattedPriceDisplay>
    );
  }

  return (
    <StyledFormattedPriceDisplay className="price">
      {!hideLeadInTxt && PRICE_LEAD_IN_TEXT && (
        <StyledTypography component="p" variant="body4" pb={1} sx={{ height: "20px" }} className="price-leadInText">
          {PRICE_LEAD_IN_TEXT}
        </StyledTypography>
      )}

      {!hideCrossedPrice && IS_PROMOTION && (
        <StyledBox className="price-markedPrice product-price-promo">
          {MARKED_PRICE && (
            <StyledTypography variant="caption" component="p" className="line-through">
              {IntlPrice.format(MARKED_PRICE)}
            </StyledTypography>
          )}
          {!hidePromoPercentage && PROMO_PERCENTAGE && (
            <StyledTypography
              variant="body3"
              component="p"
              p={0.5}
              mx={1}
              className="price-discountPercentage product-price-promo-reduc">
              -{PROMO_PERCENTAGE}%
            </StyledTypography>
          )}
        </StyledBox>
      )}

      <StyledTypography variant={variant} component="span" color={COLOR} className="price-sellingPrice product-price">
        {setTwoDecimal(FINAL_PRICE)}
      </StyledTypography>

      {!hideM2TextDesc && (
        <>
          {(TYPE === PRODUCT_TYPE.COLISE || TYPE === PRODUCT_TYPE.BOTTE) && (
            <StyledTypography variant="subtitle2" component="span" className="price-perM2">
              {" / m"}
              <sup>2</sup>
            </StyledTypography>
          )}
          {METER_SIZE && METER_PRICE && (
            <StyledTypography variant="caption" component="p" pb={1} className="price-m2details">
              {`Soit ${IntlPrice.format(METER_PRICE)} `}
              {TYPE === PRODUCT_TYPE.BOTTE && "la botte de "}
              {TYPE === PRODUCT_TYPE.COLISE && "la boite de "}
              {IntlUnit.format(METER_SIZE)}
              {"²"}
            </StyledTypography>
          )}
        </>
      )}

      {ECOPART && isPLP === false && (
        <StyledTypography variant="caption" component="p" className="price-ecopart">
          {`dont ${IntlPrice.format(ECOPART)} `}
          <u>d’éco-part</u>
        </StyledTypography>
      )}

      {IS_REMISED_PRICE_VALID && (REMISED_OLD_PRICE_END_DATE || REMISED_OLD_PRICE) && (
        <Box className="price-remisedPrice">
          {REMISED_OLD_PRICE_END_DATE && (
            <StyledTypography variant="caption" component="p" className="price-remisedPrice-oldPriceEndDate">
              <Box display="flex" alignItems="center" fontWeight="bold">
                <RemisedPriceDropIcon height="25px" width="25px" />
                {REMISED_OLD_PRICE && (
                  <StyledTypography variant="caption" component="p" className="price-remisedPrice-oldPrice">
                    {`Ancien prix: ${IntlPrice.format(REMISED_OLD_PRICE)}`}
                  </StyledTypography>
                )}
                <Tooltip title={`Prix en baisse, valable depuis le ${REMISED_OLD_PRICE_END_DATE}`} sx={{ml:1}}>
                  <InfoOutlinedIcon fontSize="small"/>
                </Tooltip>
              </Box>
            </StyledTypography>
          )}
        </Box>
      )}
    </StyledFormattedPriceDisplay>
  );
};

// THIS IS DEFAULT BUNDLE CODE PRICE COMPONENT
interface FormattedPriceDisplayProps {
  min?: number | string | null | void;
  color?: string;
  productPartNumber?: string;
  variant?: string;
  sx?: any;
  currency?: string;
  sup?: boolean;
  className?: string;
  type?: string;
}
const FormattedPriceDisplay = ({
  min = null,
  variant = "subtitle1",
  color = palette.text.primary,
  sup = false,
  sx = {},
  type,
  className = "",
}: FormattedPriceDisplayProps) => {
  return (
    <StyledFormattedPriceDisplay>
      {min != null ? (
        <StyledTypography
          variant={variant}
          sx={sx}
          component="span"
          color={color}
          className={`product-price ${className}`}>
          {setTwoDecimal(min, sup)}
          {(type === PRODUCT_TYPE.COLISE || type === PRODUCT_TYPE.BOTTE) && (
            <StyledTypography variant="subtitle2" component="span" className="">
              {" / m"}
              <sup>2</sup>
            </StyledTypography>
          )}
        </StyledTypography>
      ) : (
        <StyledTypography
          variant={variant}
          sx={sx}
          component="span"
          color={color}
          className={`product-price ${className}`}>
          Produit disponible uniquement en magasin
        </StyledTypography>
      )}
    </StyledFormattedPriceDisplay>
  );
};

///////////////////////////////////////////////////////
// TODO: REPLACE THIS FormattedProductPriceDisplay COMPONENT WITH PriceDisplay from above
interface FormattedProductPriceDisplayProps {
  min?: number | null | void;
  offerPrice?: number;
  promotionalOfferPrice?: number | null | void;
  promotionalMin?: number | null | void;
  prices?: any[];
  pourcentPromo?: number;
  productPartNumber?: string;
  fromCartConfirmation?: boolean;
  isFromOrderConfirmation?: boolean;
  meterPrice?: number | null | any;
  meterSize?: number | null | any;
  ecopart?: number | null;
  color?: string;
  currency?: string;
  locale?: string;
  type?: string;
  variant?: string;
  showPriceIsNull?: boolean;
  hideInformation?: boolean;
  hideCrossedPrice?: boolean;
  hidePromoPrice?: boolean;
  remisedPriceDetail?: any;
  quantity?: number;
}

export const FormattedProductPriceDisplay: React.FC<FormattedProductPriceDisplayProps> = ({
  variant = "h1",
  offerPrice = 0,
  promotionalOfferPrice = null,
  promotionalMin = null,
  min = null,
  prices,
  productPartNumber,
  meterPrice,
  meterSize,
  pourcentPromo,
  type,
  ecopart,
  showPriceIsNull = false,
  color = palette.text.primary,
  hideInformation = false,
  hideCrossedPrice = false,
  fromCartConfirmation,
  isFromOrderConfirmation,
  hidePromoPrice = false,
  remisedPriceDetail,
  quantity = 1,
}) => {
  const currency = "EUR";
  const { t } = useTranslation();
  const startInfoCustomMade = t("PriceDisplay.StartInfo.CustomMade");
  // const startInfoOther = t("PriceDisplay.StartInfo.Other");
  const language = "fr-FR";
  const information = type && !hideInformation ? (type === PRODUCT_TYPE.SUR_MESURE ? startInfoCustomMade : null) : null;
  const priceArray = prices && getPrices(prices);
  const offer =
    promotionalOfferPrice != null
      ? promotionalOfferPrice
      : priceArray
      ? priceArray.offer !== priceArray.list
        ? priceArray.offer
        : 0
      : offerPrice !== min
      ? offerPrice
      : 0;
  const list =
    promotionalMin != null
      ? promotionalMin
      : promotionalOfferPrice != null
      ? priceArray?.offer
      : priceArray
      ? priceArray.list
      : min;

  const markedPrice = priceArray?.list || 0;

  return (
    <StyledFormattedPriceDisplay>
      {list != null && list > 0 && information && (
        <StyledTypography component="p" variant="body4" pb={1} sx={{ height: "20px" }}>
          {list != null && list > 0 && information}
        </StyledTypography>
      )}

      {list ? (
        <>
          {/* {offer > 0 && (offer < list || offer < markedPrice) && !hideCrossedPrice && (
            <StyledBox className="product-price-promo">
              <StyledTypography variant="caption" component="p" className="line-through">
                {offer > 0 && (offer < list || offer < markedPrice) && (
                  <span id={`product_price_${productPartNumber}`} className="">
                    {Intl.NumberFormat(language, {
                      style: "currency",
                      currency,
                    }).format((markedPrice ? markedPrice : list) * quantity)}
                  </span>
                )}
              </StyledTypography>

              {offer > 0 && pourcentPromo != null && pourcentPromo !== 0 && (
                <StyledTypography variant="body3" component="p" p={0.5} mx={1} className="product-price-promo-reduc">
                  {pourcentPromo > 0 && "-"}
                  {pourcentPromo}%
                </StyledTypography>
              )}
            </StyledBox>
          )} */}

          {list > 0 && !hidePromoPrice && (
            <StyledBox component="div" pb={1} m={0} className="product-price-container">
              <>
                {
                  <>
                    {type === PRODUCT_TYPE.COLISE || type === PRODUCT_TYPE.BOTTE ? (
                      <>
                        {fromCartConfirmation ? (
                          <>
                            {offer && offer !== 0 ? (
                              <StyledTypography
                                variant={variant}
                                component="span"
                                color={palette.primary.main}
                                className={`product-price`}>
                                {setTwoDecimal(meterPrice * quantity)}
                              </StyledTypography>
                            ) : (
                              <StyledTypography
                                variant={variant}
                                component="span"
                                color={color}
                                className={`product-price`}>
                                {setTwoDecimal(meterPrice * quantity)}
                              </StyledTypography>
                            )}
                          </>
                        ) : (
                          <>
                            {offer && offer !== 0 ? (
                              <StyledTypography
                                variant={variant}
                                component="span"
                                color={palette.primary.main}
                                className={`product-price`}>
                                {setTwoDecimal(offer * quantity)}
                              </StyledTypography>
                            ) : (
                              <StyledTypography
                                variant={variant}
                                component="span"
                                color={color}
                                className={`product-price`}>
                                {setTwoDecimal(list * quantity)}
                              </StyledTypography>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {offer && offer !== 0 ? (
                          <StyledTypography
                            variant={variant}
                            component="span"
                            color={palette.primary.main}
                            className={`product-price`}>
                            {setTwoDecimal(offer * quantity)}
                          </StyledTypography>
                        ) : (
                          <StyledTypography
                            variant={variant}
                            component="span"
                            color={color}
                            className={`product-price`}>
                            {setTwoDecimal(list * quantity)}
                          </StyledTypography>
                        )}
                      </>
                    )}

                    {(type === PRODUCT_TYPE.COLISE || type === PRODUCT_TYPE.BOTTE) &&
                      !fromCartConfirmation &&
                      !isFromOrderConfirmation && (
                        <StyledTypography variant="subtitle2" component="span" className="">
                          {" / m"}
                          <sup>2</sup>
                        </StyledTypography>
                      )}
                  </>
                }
              </>
            </StyledBox>
          )}
          {meterSize != null && meterPrice != null && !fromCartConfirmation && (
            <StyledTypography variant="caption" component="p" pb={1} className="">
              Soit{" "}
              {Intl.NumberFormat(language, {
                style: "currency",
                currency,
              }).format(meterPrice * quantity)}{" "}
              {type === PRODUCT_TYPE.BOTTE && "la botte de "}
              {type === PRODUCT_TYPE.COLISE && "la boite de "}
              {Intl.NumberFormat(language, {
                style: "unit",
                unit: "meter",
              }).format(storeUtil.parseFloatDecimal(meterSize, 3))}
              {"²"}
            </StyledTypography>
          )}

          {ecopart != null && ecopart > 0 && (
            <StyledTypography variant="caption" component="p" className="eco-part-price">
              dont{" "}
              {Intl.NumberFormat(language, {
                style: "currency",
                currency,
              }).format(ecopart)}{" "}
              <u>d’éco-part</u>
            </StyledTypography>
          )}
          {remisedPriceDetail && remisedPriceDetail.OldPriceEnd && remisedPriceDetail.OldPriceEnd !== "null" && (
            <StyledTypography variant="caption" component="p" className="old-price-end-date">
              Prix en baisse, valable depuis le {getDateMonth(remisedPriceDetail.OldPriceEnd, true, "DD/MM/YYYY")}
            </StyledTypography>
          )}
          {remisedPriceDetail && remisedPriceDetail.oldPrice && remisedPriceDetail.oldPrice !== "null" && (
            <StyledTypography variant="caption" component="p" className="old-price">
              Ancien prix:{" "}
              {Intl.NumberFormat(language, {
                style: "currency",
                currency,
              }).format(remisedPriceDetail.oldPrice * quantity)}
            </StyledTypography>
          )}
        </>
      ) : (
        <>
          {showPriceIsNull && (
            <StyledTypography
              variant={"body4"}
              component="p"
              color={color}
              pb={1}
              className={`product-price`}
              sx={{ height: 92 }}>
              Produit disponible uniquement en magasin
            </StyledTypography>
          )}
        </>
      )}
    </StyledFormattedPriceDisplay>
  );
};
///////////////////////////////////////////////////////

export default FormattedPriceDisplay;
